import { useLocation, useNavigate } from "react-router";

import { t } from "i18next";

import { Container, Grid, SxProps, Typography } from "@mui/material";
import { Box } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { userAuctionsBadgesAtom } from "@/src/state/userAuctionsBadgesAtom";

import { BaanaLogo } from "@/src/components/atoms";
import FooterLink from "@/src/components/organisms/Footer/FooterLink";

import ContentRouterWidget from "@almamedia/ict-content-router-react";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";
import { getBaseUrl } from "@/src/utils/baseUrlUtils";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtom } from "jotai";

const AlmaFooter = ContentRouterWidget;

const Footer = () => {
  const { isAuthenticated, isConsumer, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const gtmSendEvent = useGTMDispatch();
  const [userAuctionsBadges] = useAtom(userAuctionsBadgesAtom);
  const draftAuction = userAuctionsBadges?.draftAuctionId;

  const onClickPlaceBid = () => {
    if (!isAuthenticated) {
      gtmSendEvent({
        event: "frontpageLoginClicked",
        value: "consumer"
      });
      login("consumer", LoginSource.FOOTER_CTA);
      // If logged in and there is a draft, direct to editing draft
    } else if (isAuthenticated && draftAuction) {
      navigate("/auction/edit/" + draftAuction);
      // If logged in but no draft, direct to new auction
      // Users can have multiple auctions, so we don't block based on active auctions
    } else if (isAuthenticated && !draftAuction) {
      navigate("/auction/edit");
    }
  };

  let placeBidButtonText = t("components.footer.placeABid");
  if (draftAuction) {
    placeBidButtonText = t("components.footer.continueAuction");
  }

  // Show footer only for consumer and dealer users
  if (location.pathname.startsWith("/admin")) {
    return null;
  }

  const isDealer = location.pathname.startsWith("/dealer");

  const headingStyles: SxProps = {
    mb: 1.5,
    lineHeight: "1.25rem",
    color: theme.palette.common.white
  };

  return (
    <>
      <Container
        component="footer"
        maxWidth={false}
        sx={{
          justifyContent: "center",
          backgroundImage: `linear-gradient(${theme.palette.text.primary} 0%, ${theme.palette.text.primary} 50%, ${theme.palette.semantic.secondary.main} 85%, ${theme.palette.semantic.secondary.main} 90%, ${theme.palette.common.black} 100%)`,
          px: 4,
          py: 3,
          borderBottom: "2px solid",
          borderColor: theme.palette.common.white
        }}
      >
        <Grid sx={{ display: "flex", justifyContent: "center" }}>
          <Grid
            container
            spacing={2}
            sx={{ width: "100%", maxWidth: "100rem" }}
          >
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 2
              }}
            >
              <Box
                sx={{
                  pr: 3,
                  maxWidth: "10rem",
                  ">svg": {
                    borderRight: [
                      "none",
                      `2px solid ${theme.palette.semantic.primary.main}`
                    ],
                    pr: 2,
                    pt: 0.25
                  }
                }}
              >
                <BaanaLogo
                  color={theme.palette.semantic.primary.contrastText}
                />
              </Box>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 2
              }}
            >
              <Typography variant="h3" sx={headingStyles}>
                {t("components.footer.aboutUsHeader")}
              </Typography>
              <FooterLink
                text={t("components.footer.contact")}
                href={
                  isDealer
                    ? `${getBaseUrl()}/dealer/contact-info`
                    : `${getBaseUrl()}/contact-info`
                }
              />
              <FooterLink
                text={t("components.footer.aboutUs")}
                href="https://www.almamobility.fi/tuotteet/baana/"
                newTab
              />
              <FooterLink
                text={t("components.footer.termsOfUse")}
                href="https://www.almamedia.fi/markkinapaikkaehdot/"
                newTab
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 2
              }}
            >
              <Typography variant="h3" sx={headingStyles}>
                {t("components.footer.baanaHeader")}
              </Typography>
              {!isDealer && (
                <FooterLink
                  text={placeBidButtonText}
                  onClick={onClickPlaceBid}
                />
              )}
              <FooterLink
                text={
                  isDealer
                    ? t("components.footer.forConsumers")
                    : t("components.footer.forDealers")
                }
                href={isDealer ? `${getBaseUrl()}/` : `${getBaseUrl()}/dealer`}
              />
              {isDealer ? (
                <FooterLink
                  text={t("components.footer.userGuide")}
                  href="assets/docs/Baana_kayttoohje_autoliikkeille.pdf"
                  sx={{
                    display: "none"
                  }}
                />
              ) : (
                <FooterLink
                  text={t("components.footer.faq")}
                  href={`${getBaseUrl()}/faq`}
                />
              )}
            </Grid>

            {(!isAuthenticated || isConsumer) && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 3
                  }}
                >
                  <Typography variant="h3" sx={headingStyles}>
                    {t("components.footer.weBuyCars")}
                  </Typography>
                  <FooterLink
                    text={t("components.footer.weBuyCars")}
                    href={`${getBaseUrl()}/ostetaan-autoja`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByCity.helsinki")}
                    href={`${getBaseUrl()}/ostetaan-autoja-helsinki`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByCity.espoo")}
                    href={`${getBaseUrl()}/ostetaan-autoja-espoo`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByCity.vantaa")}
                    href={`${getBaseUrl()}/ostetaan-autoja-vantaa`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByCity.turku")}
                    href={`${getBaseUrl()}/ostetaan-autoja-turku`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByCity.tampere")}
                    href={`${getBaseUrl()}/ostetaan-autoja-tampere`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByCity.oulu")}
                    href={`${getBaseUrl()}/ostetaan-autoja-oulu`}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 3
                  }}
                >
                  <Typography variant="h3" sx={headingStyles}>
                    {t("components.footer.weBuyCarsByMaker")}
                  </Typography>
                  <FooterLink
                    text={t("components.footer.weBuyCarsByMakerLinks.audi")}
                    href={`${getBaseUrl()}/ostetaan-autoja-audi`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByMakerLinks.bmw")}
                    href={`${getBaseUrl()}/ostetaan-autoja-bmw`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByMakerLinks.ford")}
                    href={`${getBaseUrl()}/ostetaan-autoja-ford`}
                  />
                  <FooterLink
                    text={t(
                      "components.footer.weBuyCarsByMakerLinks.mercedesBenz"
                    )}
                    href={`${getBaseUrl()}/ostetaan-autoja-mercedes-benz`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByMakerLinks.nissan")}
                    href={`${getBaseUrl()}/ostetaan-autoja-nissan`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByMakerLinks.opel")}
                    href={`${getBaseUrl()}/ostetaan-autoja-opel`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByMakerLinks.skoda")}
                    href={`${getBaseUrl()}/ostetaan-autoja-skoda`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByMakerLinks.toyota")}
                    href={`${getBaseUrl()}/ostetaan-autoja-toyota`}
                  />
                  <FooterLink
                    text={t(
                      "components.footer.weBuyCarsByMakerLinks.volkswagen"
                    )}
                    href={`${getBaseUrl()}/ostetaan-autoja-volkswagen`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByMakerLinks.volvo")}
                    href={`${getBaseUrl()}/ostetaan-autoja-volvo`}
                  />
                  <FooterLink
                    text={t("components.footer.weBuyCarsByMakerLinks.tesla")}
                    href={`${getBaseUrl()}/ostetaan-autoja-tesla`}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Container>
      <Container
        component="footer"
        maxWidth={false}
        sx={{
          backgroundColor: theme.palette.semantic.secondary.main,
          p: [2, 3]
        }}
      >
        <Container>
          <AlmaFooter slot="ALMA_FOOTER" />
        </Container>
      </Container>
    </>
  );
};

export default Footer;
